/**
 * SiteMarketRoute — the element of `/project/:id/market` (djin-cloud#1089, spec §5.2).
 *
 * Eager and small on purpose: it decides from the layout's site whether the Market page
 * may render at all, so a site without a market never downloads the page's chunk.
 *
 *  - A site with no market descriptor (or no site in the layout) replaces the URL with
 *    `/project/:id`: a direct link to a market that does not exist lands on the
 *    Overview, with no error page.
 *  - Otherwise SiteMarket loads lazily (the repo's first React.lazy and Suspense, so the
 *    entry chunk does not grow with the tab; slice 6a's ECharts code goes in that chunk).
 */
import { Suspense, lazy } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useProjectSummarySite } from '../hooks/useProjectSummarySite';
import { hasMarket } from '../services/market/descriptor';

const SiteMarket = lazy(() => import('./SiteMarket'));

export function SiteMarketRoute() {
  const { id = '' } = useParams<{ id: string }>();
  const site = useProjectSummarySite();

  if (!hasMarket(site)) return <Navigate to={`/project/${id}`} replace />;

  return (
    <Suspense
      fallback={
        <div className="p-6 text-sm text-ems-text-muted" data-testid="market-route-loading">
          Loading market…
        </div>
      }
    >
      <SiteMarket siteId={id} site={site} />
    </Suspense>
  );
}
